import { Suspense } from "react";

import { formatUserName } from "@impero-com/common/utils/formatting";
import { type TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import moment from "moment-timezone";

import type {
    ControlPolicy,
    ControlPolicyForUserEvent,
    UserView,
} from "api/generated/model";
import { useGetUserEventsLatestSuspense } from "api/generated/utils/utils";
import { ErrorBoundary } from "components/errorBoundary";
import {
    MAX_RECENT_CHANGES,
    type RecentChange,
    RecentChanges,
} from "components/recentChanges/RecentChanges";
import { EVENT_LOG_COLUMN, eventLogUrl } from "org/eventLog/eventLogUrl";
import {
    EventName,
    type OrganisationSettingsUserEvent,
} from "org/eventLog/types";

import { type ControlPolicyKind, optionOf } from "./controlPolicyOption";
import { controlPolicyTexts } from "./controlPolicyTexts";

type EventUsers = Record<number, Pick<UserView, "firstname" | "lastname">>;

export function ControlPolicyRecentChanges() {
    return (
        <ErrorBoundary routeKey="controlPolicyRecentChanges" inline>
            <Suspense fallback={null}>
                <LatestControlPolicyChanges />
            </Suspense>
        </ErrorBoundary>
    );
}

function LatestControlPolicyChanges() {
    const { t } = useTranslation();
    const {
        data: { content, linkedResources },
    } = useGetUserEventsLatestSuspense(
        {
            names: [EventName.CONTROL_POLICY_CHANGED],
            limit: MAX_RECENT_CHANGES,
        },
        { query: { select: (response) => response.data } }
    );
    const users: EventUsers = Object.fromEntries(
        linkedResources.map((user) => [user.id, user])
    );
    const changes = content
        .map((dto) => dto.resource.event)
        .filter(
            (event): event is OrganisationSettingsUserEvent =>
                event.name === EventName.CONTROL_POLICY_CHANGED
        )
        .map((event) => recentChangeOf(t, event, users));

    return (
        <RecentChanges
            changes={changes}
            description={t("ControlPolicies_RecentChanges_Description")}
            emptyLabel={t("ControlPolicies_RecentChanges_Empty")}
            viewAllUrl={eventLogUrl({
                column: EVENT_LOG_COLUMN.TYPE,
                value: EventName.CONTROL_POLICY_CHANGED,
            })}
        />
    );
}

function recentChangeOf(
    t: TFunction,
    event: OrganisationSettingsUserEvent,
    users: EventUsers
): RecentChange {
    const from = policyOf(event.oldValue);
    const to = policyOf(event.newValue);
    const texts = controlPolicyTexts(t, to.kind);
    const created = moment(event.created);
    return {
        id: event.id,
        created: event.created,
        actor: {
            type: "user",
            name: formatUserName(users[event.userId]),
            url: eventLogUrl({
                range: {
                    from: created.clone().startOf("day"),
                    to: created.clone().endOf("day"),
                },
                column: EVENT_LOG_COLUMN.ID,
                value: event.id.toString(),
            }),
        },
        field: texts.title,
        values: {
            from: texts.options[optionOf(from.policy)].label,
            to: texts.options[optionOf(to.policy)].label,
        },
    };
}

function policyOf(value: ControlPolicyForUserEvent): {
    kind: ControlPolicyKind;
    policy: ControlPolicy;
} {
    return "loginProtection" in value
        ? { kind: "loginProtection", policy: value.loginProtection }
        : { kind: "segregationOfDuties", policy: value.segregationOfDuties };
}
